<template>
	<FormGroup :label="label" :help-text="helpText">
		<div class="relative">
			<div class="flex gap-1.5 items-center">
				<!-- URL Input -->
				<input
					ref="input"
					type="text"
					class="flex-1 block w-full min-w-0 px-3 border h-11 focus:ring-black focus:outline-none rounded-xl bg-neutral-100 placeholder:text-gray-500"
					:class="[hasError ? '!border-red-300' : '!border-gray-200']"
					:value="value"
					:placeholder="placeholder"
					:disabled="disabled"
					:required="required"
					@input="handleInput"
					@focus="handleFocus"
					@blur="handleBlur"
				/>

				<!-- Clear Button (Append) -->
				<div v-if="value && !disabled" class="inline-flex items-center">
					<button
						type="button"
						class="inline-flex items-center justify-center text-gray-500 w-7 h-7 rounded-xl hover:bg-neutral-100 focus:outline-none focus:ring-none"
						@click="clearInput"
						:aria-label="$t('crud.clear')"
					>
						<Icon :icon="['far', 'times']" class="w-4 h-4" />
					</button>
				</div>
			</div>

			<!-- Error Message -->
			<p v-if="validationMessage" class="mt-1 text-sm text-red-600">
				{{ validationMessage }}
			</p>
		</div>
	</FormGroup>
</template>

<script>
import FormGroup from '~/components/ui/form/FormGroup'
import Icon from '~/components/ui/common/Icon'

export default {
	name: 'LinkedInUrlInput',
	components: {
		FormGroup,
		Icon
	},
	props: {
		value: {
			type: String,
			default: ''
		},
		label: {
			type: String,
			default: null
		},
		placeholder: {
			type: String,
			default: 'https://www.linkedin.com/in/your-profile'
		},
		helpText: {
			type: String,
			default: null
		},
		disabled: {
			type: Boolean,
			default: false
		},
		required: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			validationMessage: null,
			hasError: false,
			hasFocused: false
		}
	},
	methods: {
		sanitizeUrl(url) {
			if (!url) return ''

			let sanitized = url.trim()

			// Remove any HTML tags
			sanitized = sanitized.replace(/<[^>]*>/g, '')

			// Decode URL-encoded characters to prevent bypass attempts
			// Do this multiple times to catch nested encoding (e.g., %25%36%33 -> %63 -> c)
			let decoded = sanitized
			let previousDecoded = ''
			let iterations = 0
			const maxIterations = 3 // Prevent infinite loops

			while (decoded !== previousDecoded && iterations < maxIterations) {
				previousDecoded = decoded
				try {
					decoded = decodeURIComponent(decoded)
				} catch (e) {
					// If decoding fails (malformed URL), use the last valid decoded version
					break
				}
				iterations++
			}

			// Check decoded URL against dangerous protocols
			const decodedLower = decoded.toLowerCase()
			const dangerousProtocols = [
				'javascript:',
				'data:',
				'vbscript:',
				'about:',
				'file:'
			]

			for (const protocol of dangerousProtocols) {
				if (decodedLower.startsWith(protocol)) {
					return ''
				}
			}

			// Remove any non-printable characters and control characters
			sanitized = sanitized.replace(/[\x00-\x1F\x7F-\x9F]/g, '')

			return sanitized
		},
		handleInput(event) {
			const rawValue = event.target.value
			const sanitizedValue = this.sanitizeUrl(rawValue)

			this.$emit('input', sanitizedValue)

			// Validate on input to ensure parent component state updates immediately
			// Don't show error messages while typing (showMessage = false)
			this.validate(sanitizedValue, false)
		},
		handleFocus() {
			this.hasFocused = true
		},
		handleBlur() {
			// Only validate on blur if field was focused
			if (this.hasFocused) {
				// Show error messages on blur (showMessage = true)
				this.validate(this.value, true)
			}
			this.$emit('blur')
		},
		clearInput() {
			this.$emit('input', '')
			this.validationMessage = null
			this.hasError = false
			this.$emit('validation', false)
		},
		validate(url, showMessage = true) {
			// If empty
			if (!url || url.trim() === '') {
				if (this.required) {
					if (showMessage) {
						this.validationMessage = this.$t(
							'linkedin.import.error.invalid_url'
						)
						this.hasError = true
					}
					this.$emit('validation', false)
					return false
				} else {
					this.validationMessage = null
					this.hasError = false
					this.$emit('validation', true)
					return true
				}
			}

			// Check valid LinkedIn URL format
			const trimmedUrl = url.trim()
			const validPrefixes = [
				'https://www.linkedin.com/',
				'http://www.linkedin.com/',
				'www.linkedin.com/'
			]

			const isValid = validPrefixes.some(prefix =>
				trimmedUrl.startsWith(prefix)
			)

			if (!isValid) {
				if (showMessage) {
					this.validationMessage = this.$t(
						'linkedin.import.error.invalid_url'
					)
					this.hasError = true
				}
				this.$emit('validation', false)
				return false
			}

			// Valid
			this.validationMessage = null
			this.hasError = false
			this.$emit('validation', true)
			return true
		},
		getValidationState() {
			return !this.hasError
		}
	},
	mounted() {
		// Validate immediately if there's already a value
		if (this.value && this.value.trim() !== '') {
			this.validate(this.value)
		}
	}
}
</script>
